import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Button,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/Context';

import toast from 'react-hot-toast';
import { SocketNFCContext } from '../../context/SocketNFC';
import { SocketContext } from '../../context/SocketContext';
import { useTranslation } from 'react-i18next';
import TouchButton from '../../components/TouchButton/TouchButton';
import SearchIcon from '@mui/icons-material/Search';
import {
  headerCellSx,
  dataRowSx,
  touchMenuProps,
} from '../../theme/tableTokens';

import TableLoader from '../../components/TableLoader/TableLoader';
import '../../assets/css/buttons.css';
import { timeOfDay, todayLocal } from '../../utils/formatDate';


const PROVIDER_COLORS = {
  CH: { bg: '#0d3b7a', color: '#90caf9' },
  WG: { bg: '#4a1070', color: '#ce93d8' },
  'OK D.': { bg: '#004d40', color: '#80cbc4' },
};

function SisteTransaksjoner() {
  const { hall } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const { t } = useTranslation();

  const navigate = useNavigate();
  const { isLogin, isBlocking, BlockingScreen } = useContext(AppContext);

  const inputRef = useRef(null);
  const dateInputRef = useRef(null);

  const [rows, setRows] = useState(false);
  const [rowsFiltered, setRowsFiltered] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [searchType, setSearchType] = useState('ticketID');
  const searchTypeLabel = {
    username: t('sisteSalgs.username'),
    name: t('pep.name'),
    userID: t('sisteSalgs.userID'),
    ticketID: t('sales.ticketSaleID'),
    provider: t('sales.provider'),
    description: t('sisteTrans.description'),
    time: t('sales.time'),
  };
  const [searchValue, setSearchValue] = useState('');
  const [date, setDate] = useState(todayLocal());

  const handleSearch = () => {
    setIsLoading(true);
    socket.emit('getLastTransactions', { bingoName: hall, date });
  };

  useEffect(() => {
    if (!rows) return;
    let filtered = Array.isArray(rows) ? [...rows] : [];
    if (searchValue.trim()) {
      const val = searchValue.trim().toLowerCase();
      const guestLabel = t('kiosk.guest').toLowerCase();
      if (searchType === 'username') {
        filtered = filtered.filter((row) => {
          const effective =
            row.username && row.username !== ''
              ? row.username.toLowerCase()
              : guestLabel;
          return effective.includes(val);
        });
      } else if (searchType === 'name') {
        filtered = filtered.filter((row) => {
          const effective =
            row.name && row.name !== '' ? row.name.toLowerCase() : guestLabel;
          return effective.includes(val);
        });
      } else if (searchType === 'userID') {
        filtered = filtered.filter((row) => {
          const effective = row.userID === 0 ? '-' : String(row.userID);
          return effective.includes(val);
        });
      } else if (searchType === 'ticketID') {
        filtered = filtered.filter(
          (row) =>
            String(row.ticketID || '').includes(val) ||
            String(row.ticket7Pincode || '').includes(val)
        );
      } else if (searchType === 'provider') {
        filtered = filtered.filter((row) =>
          row.provider?.toLowerCase().includes(val)
        );
      } else if (searchType === 'description') {
        filtered = filtered.filter((row) =>
          row.description?.toLowerCase().includes(val)
        );
      } else if (searchType === 'time') {
        filtered = filtered.filter((row) =>
          timeOfDay(row.date).includes(val)
        );
      }
    }
    setRowsFiltered(filtered);
  }, [rows, searchType, searchValue]);

  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 1100);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isLogin) {
      navigate('/login');
    }
  }, []);

  useEffect(() => {
    socket.emit('getLastTransactions', {
      bingoName: hall,
      date: todayLocal(),
    });
  }, [hall]);

  useEffect(() => {
    socket.on('getLastTransactions', (data) => {
      setRows(data || []);
      setIsLoading(false);
    });
  }, [socket]);

  const socketNFC = useContext(SocketNFCContext);

  useEffect(() => {
    const handleScannedBarcode = (data) => {
      //console.log('handleScannedBarcode', data);
      //PINCODE
      setSearchValue(data);
    };

    const handleScannedQR = (data) => {
      //console.log('handleScannedQR', data);

      if (!data.includes('qr.clubhouse.no')) {
        toast.error(t('sales.invalidTicket'));
        return;
      }
      const urlMatch = data.match(/https?:\/\/[^\s]+/);
      if (!urlMatch) {
        toast.error(t('sales.invalidTicketUrl'));
        return;
      }

      const url = new URL(data); // Parse the URL
      const params = new URLSearchParams(url.search); // Extract the query string

      if (params.has('okd')) {
        setSearchValue(params.get('okd'));
      }

      if (params.has('wg')) {
        setSearchValue(params.get('wg'));
      }

      if (params.has('id')) {
        setSearchValue(params.get('id'));
      }
    };

    socketNFC.on('scannedBarcode', handleScannedBarcode);
    socketNFC.on('scannedQR', handleScannedQR);

    return () => {
      socketNFC.off('scannedBarcode', handleScannedBarcode);
      socketNFC.off('scannedQR', handleScannedQR);
    };
  }, [socketNFC]);

  return (
    <>
      {isBlocking && <BlockingScreen />}
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: '100%',
          maxHeight: '100%',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            width: '100%',
            height: '100%',
          }}
        >
          <Grid
            container
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {/* SEARCH BAR */}
            <Grid
              item
              xs={12}
              sx={{
                height: '190px',
                maxHeight: '190px',
                display: 'flex',
                alignItems: 'center',
                px: 3,
                gap: 2,
                borderBottom: '1px solid #2a3a6e',
              }}
            >
              <FormControl
                sx={{
                  minWidth: 180,
                  backgroundColor: '#232a45',
                  borderRadius: '4px',
                }}
              >
                <InputLabel
                  sx={{ color: 'white !important', fontSize: '20px' }}
                >
                  {t('ticketHistoric.searchType')}
                </InputLabel>
                <MuiSelect
                  MenuProps={touchMenuProps}
                  value={searchType}
                  label={t('ticketHistoric.searchType')}
                  onChange={(e) => setSearchType(e.target.value)}
                  sx={{
                    color: 'white',
                    height: '90px',
                    fontSize: '24px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#365ab8',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fcad89',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#fcad89',
                    },
                    '& .MuiSvgIcon-root': { color: 'white' },
                  }}
                >
                  <MenuItem value="username">
                    {t('sisteSalgs.username')}
                  </MenuItem>
                  <MenuItem value="name">{t('pep.name')}</MenuItem>
                  <MenuItem value="userID">{t('sisteSalgs.userID')}</MenuItem>
                  <MenuItem value="ticketID">
                    {t('sales.ticketSaleID')}
                  </MenuItem>
                  <MenuItem value="provider">{t('sales.provider')}</MenuItem>
                  <MenuItem value="description">
                    {t('sisteTrans.description')}
                  </MenuItem>
                  <MenuItem value="time">{t('sales.time')}</MenuItem>
                </MuiSelect>
              </FormControl>

              <TextField
                sx={{
                  flex: 1,
                  backgroundColor: '#232a45',
                  '& .MuiOutlinedInput-root': {
                    height: '90px',
                    '& fieldset': { borderColor: '#365ab8' },
                    '&:hover fieldset': { borderColor: '#fcad89' },
                    '&.Mui-focused fieldset': { borderColor: '#fcad89' },
                  },
                  '& input': { color: 'white', fontSize: '24px' },
                  '& label': { color: 'white !important', fontSize: '20px' },
                  '& label.Mui-focused': { color: 'white !important' },
                  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                    transform: 'translate(14px, 30px) scale(1)',
                  },
                }}
                label={searchTypeLabel[searchType] || ''}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus
                autoComplete="off"
                variant="outlined"
                inputRef={inputRef}
              />

              <TextField
                type="date"
                label={t('sales.searchByDate')}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputRef={dateInputRef}
                onClick={() => dateInputRef.current?.showPicker?.()}
                sx={{
                  width: '310px',
                  backgroundColor: '#232a45',
                  cursor: 'pointer',
                  '& .MuiOutlinedInput-root': {
                    height: '90px',
                    cursor: 'pointer',
                    '& fieldset': { borderColor: '#365ab8' },
                    '&:hover fieldset': { borderColor: '#fcad89' },
                    '&.Mui-focused fieldset': { borderColor: '#fcad89' },
                  },
                  '& input': {
                    color: 'white',
                    fontSize: '24px',
                    colorScheme: 'dark',
                    cursor: 'pointer',
                  },
                  '& label': { color: 'white !important', fontSize: '20px' },
                  '& label.Mui-focused': { color: 'white !important' },
                }}
              />

              <TouchButton
                tone="confirm"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
                sx={{ height: '90px', minWidth: '160px', flexShrink: 0 }}
              >
                {t('menu.search')}
              </TouchButton>
            </Grid>
            {/*TABLE*/}
            <Grid
              item
              xs={12}
              sx={{
                width: '100%',
                padding: '10px',
                height: '100%',
              }}
            >
              {isLoading ? (
                <TableLoader />
              ) : (
                <TableContainer
                  component={Paper}
                  className="sp-scroll"
                  sx={{
                    maxHeight: 'calc(100% - 190px)',
                    marginBottom: '190px',
                    overflowY: 'auto',
                    backgroundColor: '#1a2240',
                  }}
                >
                  <Table
                    sx={{
                      color: 'white',
                      background: '#1a2240',
                    }}
                    stickyHeader
                    aria-label="simple table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sales.time')}
                        </TableCell>
                        {/*
                      <TableCell align="center">
                        {t('sisteTrans.operator')}
                      </TableCell>
                      */}
                        <TableCell align="center" sx={headerCellSx}>
                          {t('sales.platform')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sisteSalgs.userID')}
                        </TableCell>

                        <TableCell sx={headerCellSx} align="center">
                          {t('sisteSalgs.username')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('pep.name')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sisteSalgs.total')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sales.provider')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sisteTrans.description')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sales.ticketSaleID')}
                        </TableCell>
                        <TableCell sx={headerCellSx} align="center">
                          {t('sisteTrans.ticketID')}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {rowsFiltered && (
                      <TableBody>
                        {rowsFiltered.map((row, index) => (
                          <TableRow
                            key={index}
                            sx={dataRowSx}
                          >
                            <TableCell
                              component="th"
                              scope="row"
                              align="center"
                            >
                              {timeOfDay(row.date)}
                            </TableCell>

                            {/*
                              <TableCell align="center">
                                {row.operator}
                              </TableCell>
                              */}
                            <TableCell align="center">
                              <Chip
                                label={
                                  row.platform === 'kiosk'
                                    ? t('sales.platformKiosk')
                                    : t('sales.platformCashier')
                                }
                                sx={{
                                  backgroundColor:
                                    row.platform === 'kiosk'
                                      ? '#3e2723'
                                      : '#263238',
                                  color:
                                    row.platform === 'kiosk'
                                      ? '#bcaaa4'
                                      : '#b0bec5',
                                  fontWeight: 600,
                                  fontSize: '12px',
                                  height: '26px',
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              {row.userID === 0 ? '—' : row.userID}
                            </TableCell>
                            <TableCell align="center">
                              {row.username && row.username !== ''
                                ? row.username
                                : t('kiosk.guest')}
                            </TableCell>
                            <TableCell align="center">
                              {row.name && row.name !== ''
                                ? row.name
                                : t('kiosk.guest')}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ color: '#8fa3d4', fontWeight: 600 }}>
                                {row.amount}
                              </Box>
                            </TableCell>

                            <TableCell align="center">
                              {row.provider && row.provider !== '' ? (
                                <Chip
                                  label={row.provider}
                                  sx={{
                                    backgroundColor: (
                                      PROVIDER_COLORS[row.provider] || {
                                        bg: '#263238',
                                        color: '#b0bec5',
                                      }
                                    ).bg,
                                    color: (
                                      PROVIDER_COLORS[row.provider] || {
                                        bg: '#263238',
                                        color: '#b0bec5',
                                      }
                                    ).color,
                                    fontWeight: 700,
                                    fontSize: '12px',
                                    height: '26px',
                                  }}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="center">
                              {row.description}
                            </TableCell>
                            <TableCell align="center">
                              {row.ticket7Pincode === ''
                                ? '-'
                                : row.ticket7Pincode}
                            </TableCell>
                            <TableCell align="center">
                              {row.provider === 'OK D.' || row.provider === 'WG'
                                ? '-'
                                : row.ticketID === ''
                                  ? '-'
                                  : row.ticketID}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default SisteTransaksjoner;
